import React from 'react';
import PropTypes from 'prop-types';
import { FaLock, FaHeadset } from 'react-icons/fa';
import { IapUsageSkeleton } from './skeletons';

export default function FeatureGate({
  entitled = false,
  featureName = 'Feature',
  featureDescription,
  skeletonContent,
  children
}) {
  if (entitled) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative w-full flex-1 min-h-[calc(100vh-180px)] overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50/95 to-slate-100/98 flex items-center justify-center box-border"
      data-testid="feature-gate-locked"
    >
      {/* Blurred static teaser skeleton */}
      <div
        className="absolute inset-0 w-full h-full blur-[8px] opacity-75 scale-[1.02] pointer-events-none select-none overflow-hidden z-0"
        aria-hidden="true"
        data-testid="feature-gate-backdrop"
      >
        {skeletonContent || <IapUsageSkeleton featureName={featureName} />}
      </div>

      {/* Centered Paywall & Support Details Card */}
      <div className="relative z-10 w-full max-w-xl mx-5 my-10 flex justify-center animate-in fade-in slide-in-from-bottom-3 duration-300">
        <div
          className="w-full bg-white/95 backdrop-blur-xl border border-slate-200/90 rounded-2xl p-8 sm:p-11 text-center shadow-2xl ring-1 ring-indigo-500/10"
          data-testid="feature-gate-card"
        >
          <div className="w-16 h-16 mx-auto mb-4.5 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600 flex items-center justify-center shadow-md shadow-indigo-500/20">
            <FaLock className="text-2xl text-indigo-600" />
          </div>

          <h2 className="text-[22px] font-bold text-slate-900 mb-2 tracking-tight">
            {featureName}
          </h2>

          <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 border border-slate-200">
            Subscription Upgrade Required
          </span>

          <p className="text-sm leading-relaxed text-slate-600 mb-5">
            {featureDescription || `${featureName} is not included in your organization's current subscription tier.`}
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5 mb-6 text-[13.5px] leading-relaxed text-slate-700 text-left">
            <p className="m-0">
              Please contact <strong className="text-slate-900 font-semibold">Cognior Support</strong> to learn more about the feature benefits, plan details, and how to activate this capability for your organization.
            </p>
          </div>

          <div className="flex justify-center items-center gap-3">
            <a
              href={`mailto:support@cognior.com?subject=${encodeURIComponent(`Inquiry: Upgrade plan for ${featureName}`)}&body=${encodeURIComponent(`Hello Cognior Support,\n\nOur team is interested in enabling ${featureName}. Please provide more details on plan options and activation steps.\n\nThank you!`)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-br from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-600/25 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer no-underline"
              data-testid="contact-cognior-support-btn"
            >
              <FaHeadset className="text-sm" /> Contact Cognior Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

FeatureGate.propTypes = {
  entitled: PropTypes.bool,
  featureName: PropTypes.string,
  featureDescription: PropTypes.string,
  skeletonContent: PropTypes.node,
  children: PropTypes.node
};
